'use client';

import { useState, useCallback, useRef } from 'react';

type Side = 'top' | 'right' | 'bottom' | 'left';

type PositionStyle = {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  transform?: string;
};

type UsePositioningResult = {
  /** The actual side after collision detection (may differ from preferred) */
  computedSide: Side;
  /** Inline styles to position the floating element */
  positionStyle: PositionStyle;
  /** Inline styles to position the arrow element */
  arrowStyle: PositionStyle;
  /** Call this to recalculate position (e.g. when tooltip becomes visible) */
  updatePosition: () => void;
  /** Ref to attach to the wrapper element */
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  /** Ref to attach to the floating element (tooltip/popover) */
  floatingRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Hook for positioning floating elements (tooltips, popovers) with
 * collision detection. Flips to the opposite side when there isn't
 * enough room, and shifts horizontally/vertically to stay in viewport.
 *
 * @param options - Positioning configuration
 * @returns Position data, refs, and an update function
 *
 * @example
 * const { computedSide, positionStyle, arrowStyle, updatePosition, wrapperRef, floatingRef } =
 *   usePositioning({ side: 'top', gap: 8 });
 */
type UsePositioningOptions = {
  /** Preferred placement side */
  side?: Side;
  /** Gap between trigger and floating element in px */
  gap?: number;
  /** Padding from viewport edges in px */
  viewportPadding?: number;
  /** Whether positioning is active */
  enabled?: boolean;
};

const usePositioning = ({
  side = 'top',
  gap = 8,
  viewportPadding = 8,
  enabled = true,
}: UsePositioningOptions = {}): UsePositioningResult => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const floatingRef = useRef<HTMLDivElement | null>(null);

  const [computedSide, setComputedSide] = useState<Side>(side);
  const [positionStyle, setPositionStyle] = useState<PositionStyle>({});
  const [arrowStyle, setArrowStyle] = useState<PositionStyle>({});

  const updatePosition = useCallback(() => {
    if (!enabled || !wrapperRef.current || !floatingRef.current) return;

    const triggerRect = wrapperRef.current.getBoundingClientRect();
    const floatingRect = floatingRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Determine the best side (flip if not enough space)
    let resolvedSide = side;

    if (side === 'top' || side === 'bottom') {
      const spaceAbove = triggerRect.top;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const needed = floatingRect.height + gap;

      if (side === 'top' && spaceAbove < needed && spaceBelow > spaceAbove) {
        resolvedSide = 'bottom';
      } else if (side === 'bottom' && spaceBelow < needed && spaceAbove > spaceBelow) {
        resolvedSide = 'top';
      }
    } else {
      const spaceLeft = triggerRect.left;
      const spaceRight = viewportWidth - triggerRect.right;
      const needed = floatingRect.width + gap;

      if (side === 'left' && spaceLeft < needed && spaceRight > spaceLeft) {
        resolvedSide = 'right';
      } else if (side === 'right' && spaceRight < needed && spaceLeft > spaceRight) {
        resolvedSide = 'left';
      }
    }

    setComputedSide(resolvedSide);

    // Calculate position relative to wrapper (since tooltip is position:absolute inside wrapper)
    const triggerWidth = triggerRect.width;
    const triggerHeight = triggerRect.height;
    const floatingWidth = floatingRect.width;
    const floatingHeight = floatingRect.height;

    const newStyle: PositionStyle = {};
    const newArrowStyle: PositionStyle = {};
    let shiftX = 0;
    let shiftY = 0;

    if (resolvedSide === 'top' || resolvedSide === 'bottom') {
      // Center horizontally relative to trigger
      const idealLeft = (triggerWidth - floatingWidth) / 2;

      // Check viewport overflow (convert to absolute screen coords)
      const absoluteLeft = triggerRect.left + idealLeft;
      const absoluteRight = absoluteLeft + floatingWidth;

      if (absoluteLeft < viewportPadding) {
        shiftX = viewportPadding - absoluteLeft;
      } else if (absoluteRight > viewportWidth - viewportPadding) {
        shiftX = viewportWidth - viewportPadding - absoluteRight;
      }

      const finalLeft = idealLeft + shiftX;

      if (resolvedSide === 'top') {
        newStyle.bottom = `calc(100% + ${gap}px)`;
      } else {
        newStyle.top = `calc(100% + ${gap}px)`;
      }
      newStyle.left = `${finalLeft}px`;

      // Arrow: stays centered on trigger, offset by the shift
      const arrowLeft = floatingWidth / 2 - 4 - shiftX; // 4 = arrow half-size
      newArrowStyle.left = `${Math.max(8, Math.min(arrowLeft, floatingWidth - 16))}px`;
    } else {
      // left or right — center vertically
      const idealTop = (triggerHeight - floatingHeight) / 2;

      const absoluteTop = triggerRect.top + idealTop;
      const absoluteBottom = absoluteTop + floatingHeight;

      if (absoluteTop < viewportPadding) {
        shiftY = viewportPadding - absoluteTop;
      } else if (absoluteBottom > viewportHeight - viewportPadding) {
        shiftY = viewportHeight - viewportPadding - absoluteBottom;
      }

      const finalTop = idealTop + shiftY;

      if (resolvedSide === 'left') {
        newStyle.right = `calc(100% + ${gap}px)`;
      } else {
        newStyle.left = `calc(100% + ${gap}px)`;
      }
      newStyle.top = `${finalTop}px`;

      // Arrow: stays centered on trigger, offset by the shift
      const arrowTop = floatingHeight / 2 - 4 - shiftY;
      newArrowStyle.top = `${Math.max(8, Math.min(arrowTop, floatingHeight - 16))}px`;
    }

    setPositionStyle(newStyle);
    setArrowStyle(newArrowStyle);
  }, [side, gap, viewportPadding, enabled]);

  return {
    computedSide,
    positionStyle,
    arrowStyle,
    updatePosition,
    wrapperRef,
    floatingRef,
  };
};

export default usePositioning;
